import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, FileText, ShieldAlert, DollarSign, Factory } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import api from '@/lib/api';
import { AnalyticsShell } from '@/components/AnalyticsShell';

const RISK_COLORS = { very_high: '#8B0000', high: '#FF3B30', moderate: '#FF9500', low: '#2E7D32', very_low: '#0097A7', unknown: '#4E6974' };
const RISK_LABELS = { very_high: 'Very high', high: 'High', moderate: 'Moderate', low: 'Low', very_low: 'Very low', unknown: 'Unknown' };

const ModernSlaveryPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [riskFilter, setRiskFilter] = useState('all');

  useEffect(() => {
    api.getModernSlavery()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AnalyticsShell><div className="text-center py-20 text-[#4E6974]">Assessing modern slavery risk...</div></AnalyticsShell>;
  if (!data?.has_data) return <AnalyticsShell><EmptyState /></AnalyticsShell>;

  const filtered = riskFilter === 'all' ? data.supplier_assessments : data.supplier_assessments.filter(s => s.risk_level === riskFilter);

  return (
    <AnalyticsShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
        <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="slavery-title">Modern slavery risk assessment</h1>
        <p className="text-[#4E6974] mt-2">Australian Modern Slavery Act compliance. ILO labour indicators across your supply chain.</p>
      </motion.div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={<ShieldAlert className="h-5 w-5" />} label="Portfolio risk score" value={data.portfolio_risk_score} color={data.portfolio_risk_score >= 60 ? '#FF3B30' : data.portfolio_risk_score >= 40 ? '#FF9500' : '#2E7D32'} testId="slavery-portfolio-score" />
        <SummaryCard icon={<AlertTriangle className="h-5 w-5" />} label="High-risk spend" value={`${data.high_risk_pct}%`} sub={`$${(data.high_risk_spend / 1e6).toFixed(1)}M`} color={data.high_risk_pct > 20 ? '#FF3B30' : '#FF9500'} testId="slavery-high-risk" />
        <SummaryCard icon={<Users className="h-5 w-5" />} label="Suppliers assessed" value={data.total_suppliers} testId="slavery-total" />
        <SummaryCard icon={<DollarSign className="h-5 w-5" />} label="Total spend" value={`$${(data.total_spend / 1e6).toFixed(0)}M`} testId="slavery-spend" />
      </div>

      {/* Country risk chart */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Modern slavery risk by sourcing country</CardTitle></CardHeader>
        <CardContent>
          <div className="h-[350px]" data-testid="slavery-country-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.country_risks} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis type="number" domain={[0, 100]} fontSize={11} tick={{ fill: '#4E6974' }} />
                <YAxis type="category" dataKey="country_name" fontSize={11} tick={{ fill: '#4E6974' }} width={130} />
                <Tooltip formatter={v => [v, 'Risk score']} />
                <Bar dataKey="risk_score" radius={[0, 4, 4, 0]}>
                  {data.country_risks.map((c, i) => <Cell key={i} fill={RISK_COLORS[c.risk_level] || '#4E6974'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Country indicator cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {data.country_risks.slice(0, 6).map(c => (
          <Card key={c.country_code} className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid={`slavery-country-${c.country_code}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[#0D3B4F]">{c.country_name}</h3>
                <Badge className="text-white text-[10px]" style={{ backgroundColor: RISK_COLORS[c.risk_level] }}>{RISK_LABELS[c.risk_level]}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Indicator label="Forced labour" value={c.indicators?.forced_labour_risk} />
                <Indicator label="Child labour" value={c.indicators?.child_labour_prevalence} />
                <Indicator label="Freedom of assoc." value={c.indicators?.freedom_of_association} />
                <Indicator label="Debt bondage" value={c.indicators?.debt_bondage_risk} />
                <Indicator label="Transparency" value={c.indicators?.supply_chain_transparency} />
                <Indicator label="ILO conventions" value={c.indicators?.ilo_conventions_ratified} isNumber />
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E0E0E0]">
                <span className="text-xs text-[#4E6974]">{c.suppliers} suppliers</span>
                <span className="text-xs text-[#4E6974]">{c.spend_pct}% of spend</span>
                {c.manufacturing_wage_usd && <span className="text-xs text-[#4E6974]">${c.manufacturing_wage_usd}/mo mfg</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Supplier table */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold text-[#0D3B4F]">Supplier risk detail</CardTitle>
            <div className="flex gap-1 flex-wrap">
              {['all', 'very_high', 'high', 'moderate', 'low'].map(f => (
                <Button key={f} size="sm" variant={riskFilter === f ? 'default' : 'outline'}
                  className={riskFilter === f ? 'bg-[#0D3B4F] hover:bg-[#0D3B4F]/90 text-xs' : 'text-xs'}
                  onClick={() => setRiskFilter(f)} data-testid={`slavery-filter-${f}`}>
                  {f === 'all' ? 'All' : RISK_LABELS[f]}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="slavery-supplier-table">
              <thead>
                <tr className="border-b border-[#E0E0E0]">
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Supplier</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Country</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Tier</th>
                  <th className="text-center py-3 px-3 text-[#4E6974] font-medium">Risk</th>
                  <th className="text-right py-3 px-3 text-[#4E6974] font-medium">Spend</th>
                  <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Key risk factors</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB]">
                    <td className="py-3 px-3 font-medium text-[#0D3B4F]">
                      <Link to={`/suppliers/${s.id}`} className="hover:text-[#0097A7]">{s.supplier_name}</Link>
                    </td>
                    <td className="py-3 px-3 text-[#4E6974]">{s.country_name}</td>
                    <td className="py-3 px-3 text-[#4E6974] text-xs">{s.tier || '-'}</td>
                    <td className="py-3 px-3 text-center">
                      <Badge className="text-white text-[10px]" style={{ backgroundColor: RISK_COLORS[s.risk_level] }}>
                        {s.slavery_risk_score}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-right text-[#0D3B4F]">${(s.annual_spend / 1e6).toFixed(1)}M</td>
                    <td className="py-3 px-3">
                      {s.risk_factors.length > 0 ? (
                        <div className="flex flex-wrap gap-1">{s.risk_factors.slice(0, 3).map((f, i) => <Badge key={i} variant="outline" className="text-[9px] border-[#FF9500] text-[#FF9500]">{f}</Badge>)}</div>
                      ) : <span className="text-xs text-[#2E7D32]">No flags</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-8">
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Compliance recommendations</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="slavery-recommendations">
            {data.recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F5]">
                <ArrowRight className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#0D3B4F]">{r}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </AnalyticsShell>
  );
};

const Indicator = ({ label, value, isNumber }) => {
  if (!value && value !== 0) return null;
  const riskColors = { very_high: '#8B0000', high: '#FF3B30', restricted: '#FF3B30', moderate: '#FF9500', limited: '#FF9500', low: '#2E7D32', free: '#2E7D32', very_low: '#0097A7', very_high: '#8B0000' };
  const color = isNumber ? '#0D3B4F' : riskColors[value] || '#4E6974';
  return (
    <div className="flex flex-col">
      <span className="text-[#4E6974]">{label}</span>
      <span className="font-medium capitalize" style={{ color }}>{isNumber ? value : (value || '').replace(/_/g, ' ')}</span>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, sub, color, testId }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
    <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[#4E6974]">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
        <p className="text-3xl font-bold" style={{ color: color || '#0D3B4F' }}>{value}</p>
        {sub && <p className="text-xs text-[#4E6974] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  </motion.div>
);

const EmptyState = () => (
  <div className="text-center py-20 mt-16">
    <Users className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
    <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">No supplier data</h2>
    <p className="text-[#4E6974] mb-6">Upload suppliers to view modern slavery risk assessment.</p>
    <Link to="/suppliers"><Button className="bg-[#00707C] hover:bg-[#00707C]/90">Go to suppliers</Button></Link>
  </div>
);

export default ModernSlaveryPage;
